import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Error } from '@components/error/src/error';
import { InlineUnderlinedInput } from '@components/inline-underlined-input';
import { InputRadio } from '@components/input-radio';
import classNames from 'classnames';

import { type RadioListSelectionWithNew } from './radio-list.shared.type';
import { type RadioListProps } from './radio-list.type';

export const RadioList = ({
  name,
  options,
  value,
  defaultValue = null,
  onChangeSelection,
  enableNewOption = false,
  newOptionLabel = 'New option',
  newOptionPlaceholder,
  newMaxLength,
  newValue,
  defaultNewValue = '',
  newOptionHelper,
  onChangeNewValue,
  newError,
  isDisabled = false,
  className,
}: RadioListProps) => {
  const isSelectionControlled = value !== undefined;
  const [internalSelection, setInternalSelection] = useState<RadioListSelectionWithNew>(defaultValue);
  const selectedValue = isSelectionControlled ? value : internalSelection;

  const isNewValueControlled = newValue !== undefined;
  const [internalNewValue, setInternalNewValue] = useState<string>(defaultNewValue);
  const currentNewValue = isNewValueControlled ? newValue : internalNewValue;

  const newInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectedValue === 'new' && newInputRef.current) {
      newInputRef.current.focus();
    }
  }, [selectedValue]);

  const handleSelectionChange = (next: RadioListSelectionWithNew) => {
    if (!isSelectionControlled) {
      setInternalSelection(next);
    }

    onChangeSelection?.(next);
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: targetValue } = event.target;
    const nextSelection: RadioListSelectionWithNew = targetValue === 'new' ? 'new' : targetValue;

    handleSelectionChange(nextSelection);
  };

  const handleNewValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = event.target;

    if (!isNewValueControlled) {
      setInternalNewValue(nextValue);
    }

    onChangeNewValue?.(nextValue);
  };

  console.log('newError', newError);
  console.log('newOptionHelper', newOptionHelper);

  const newErrorMessage = <Error className='mt-1 text-critical-no-bg text-body-xs'>{newError}</Error>;
  const inputDescriptionHelper =
    selectedValue === 'new' ? <p className='mt-1 text-tertiary text-body-xs'>{newOptionHelper}</p> : undefined;

  return (
    <div className={classNames('flex flex-col gap-4.5', className)}>
      {options?.map((option) => (
        <InputRadio
          key={option.id}
          id={`${name ?? 'radio-list'}-${option.id}`}
          name={name}
          value={option.id}
          label={option.label}
          checked={selectedValue === option.id}
          disabled={isDisabled || option.disabled}
          onChange={handleRadioChange}
        />
      ))}

      {enableNewOption && (
        <div className='flex flex-col gap-4.5'>
          <InputRadio
            id={`${name ?? 'radio-list'}-new`}
            name={name}
            value='new'
            inputContainerClassName='w-full'
            description={newError ? newErrorMessage : inputDescriptionHelper}
            label={
              selectedValue === 'new' ? (
                <div className='flex-1'>
                  <InlineUnderlinedInput
                    ref={newInputRef}
                    value={currentNewValue}
                    onChange={handleNewValueChange}
                    placeholder={typeof newOptionPlaceholder === 'string' ? newOptionPlaceholder : undefined}
                    maxLength={newMaxLength}
                    hasError={!!newError}
                    helperMessage={newOptionHelper}
                    disabled={isDisabled}
                  />
                </div>
              ) : (
                <span className='text-subtitle-s text-accent-no-bg'>{newOptionLabel}</span>
              )
            }
            checked={selectedValue === 'new'}
            disabled={isDisabled}
            onChange={handleRadioChange}
          />
        </div>
      )}
    </div>
  );
};
